/**
 * Lead Generation workbench and operator queue HTML.
 */
import { formatCurrency, formatInteger } from "./format.js";
import { getDashboardOperator } from "./dashboard-auth.js";
import {
  escapeAttr,
  escapeHtml,
  labelize,
  pill
} from "./dashboard-html.js";
import type {
  LeadFactoryLeadGenerationQueueResponse,
  LeadFactoryLeadGenerationWorkbenchResponse,
  LeadGenerationWorkbenchLead
} from "./dashboard-lead-factory-types.js";

const leadFactorySuppressionReasons = [
  "duplicate",
  "not_relevant",
  "invalid_pdf",
  "bad_source_data",
  "manual_do_not_contact",
  "other_typed_review"
] as const;

export function leadGenerationWorkbenchHtml(workbench: LeadFactoryLeadGenerationWorkbenchResponse): string {
  return `
    <div id="lead-generation-workbench" class="lead-generation-workbench" data-lead-generation-workbench>
      <div class="panel-heading">
        <h2>Lead Generation Workbench</h2>
        <span class="subtle">${formatInteger(workbench.summary.hot)} hot · ${formatInteger(workbench.summary.warm)} warm · ${formatInteger(workbench.summary.missingAmount)} missing amount</span>
      </div>
      <div class="lead-generation-bulk-bar" data-lead-generation-bulk-bar>
        <strong data-lead-generation-selected-count>0 selected</strong>
        <input
          type="text"
          data-lead-generation-bulk-assignee
          aria-label="Bulk assignee"
          value="${escapeAttr(getDashboardOperator() || "unassigned")}"
        />
        <select data-lead-generation-bulk-reason aria-label="Bulk suppression reason">
          ${leadFactorySuppressionReasons
            .map((reason) => `<option value="${escapeAttr(reason)}"${reason === "other_typed_review" ? " selected" : ""}>${escapeHtml(labelize(reason))}</option>`)
            .join("")}
        </select>
        <button class="button secondary" type="button" data-lead-generation-bulk-action="assign">Assign selected</button>
        <button class="button ghost danger" type="button" data-lead-generation-bulk-action="suppress">Suppress selected</button>
      </div>
      <div class="lead-generation-grid">
        ${workbench.leads.slice(0, 8).map(leadGenerationCardHtml).join("") || `<p class="empty">No segmented lead-generation inventory is loaded.</p>`}
      </div>
    </div>
  `;
}

export function leadGenerationCardHtml(lead: LeadGenerationWorkbenchLead): string {
  const title = lead.property_address || lead.owner_name || lead.id;
  const sourceSignal = lead.source_confidence_segment || lead.primary_source_document_type || lead.mortgage_segment || "source_pending";
  return `
    <article class="lead-generation-card" data-lead-generation-card data-lead-id="${escapeAttr(lead.id)}">
      <div>
        <label class="lead-generation-card-select">
          <input type="checkbox" value="${escapeAttr(lead.id)}" data-lead-generation-select aria-label="Select lead ${escapeAttr(lead.id)}" />
          <span>Select</span>
        </label>
        <strong>${escapeHtml(title)}</strong>
        <small>${escapeHtml(lead.county)} · ${escapeHtml(labelize(sourceSignal))} · ${formatInteger(lead.source_document_count ?? 0)} docs</small>
        <p>${escapeHtml(lead.next_action || "Review lead evidence")}</p>
        ${leadGenerationSourcePreviewHtml(lead)}
        <div class="lead-generation-card-actions">
          <button class="button secondary" type="button" data-lead-action="detail" data-lead-id="${escapeAttr(lead.id)}">Detail</button>
          ${
            lead.primary_source_document_id
              ? `<button class="button secondary" type="button" data-source-document-review="${escapeAttr(lead.primary_source_document_id)}" data-lead-generation-source-review="${escapeAttr(lead.id)}">Review PDF</button>`
              : ""
          }
          <button class="button secondary" type="button" data-lead-action="assign" data-lead-id="${escapeAttr(lead.id)}">Assign</button>
          <button class="button ghost danger" type="button" data-lead-action="suppress" data-lead-id="${escapeAttr(lead.id)}">Suppress</button>
        </div>
      </div>
      <div class="lead-generation-card-meta">
        ${pill(labelize(lead.priority || "unscored"), lead.priority || "unscored")}
        ${pill(labelize(lead.contact_readiness), lead.contact_readiness)}
        <span>${typeof lead.mortgage_amount === "number" && lead.mortgage_amount > 0 ? formatCurrency(lead.mortgage_amount) : "Amount unknown"}</span>
      </div>
    </article>
  `;
}

export function leadGenerationQueueHtml(queue: LeadFactoryLeadGenerationQueueResponse): string {
  return `
    <div class="lead-generation-queue" data-lead-generation-queue>
      <div class="panel-heading">
        <h2>Operator Lead Queue</h2>
        <span class="subtle">${formatInteger(queue.summary.assigned)} mine · ${formatInteger(queue.summary.unassigned)} unassigned</span>
      </div>
      <div class="lead-generation-queue-grid">
        ${leadGenerationQueueLaneHtml("My Leads", queue.operator, queue.assignedLeads, "assigned")}
        ${leadGenerationQueueLaneHtml("Unassigned", "claim or suppress", queue.unassignedLeads, "unassigned")}
      </div>
    </div>
  `;
}

export function leadGenerationQueueLaneHtml(title: string, subtitle: string, leads: LeadGenerationWorkbenchLead[], lane: string): string {
  return `
    <div class="lead-generation-queue-lane" data-lead-generation-queue-lane="${escapeAttr(lane)}">
      <div class="mini-heading">
        <strong>${escapeHtml(title)}</strong>
        <span>${escapeHtml(subtitle)} · ${formatInteger(leads.length)}</span>
      </div>
      <div class="lead-generation-queue-list">
        ${leads.slice(0, 6).map((lead) => leadGenerationQueueCardHtml(lead)).join("") || `<p class="empty">No ${escapeHtml(title.toLowerCase())} leads loaded.</p>`}
      </div>
    </div>
  `;
}

export function leadGenerationQueueCardHtml(lead: LeadGenerationWorkbenchLead): string {
  const title = lead.property_address || lead.owner_name || lead.id;
  const sourceSignal = lead.source_confidence_segment || lead.primary_source_document_type || "source_pending";
  return `
    <article class="lead-generation-queue-card" data-lead-generation-queue-card data-lead-id="${escapeAttr(lead.id)}">
      <div>
        <strong>${escapeHtml(title)}</strong>
        <small>${escapeHtml(lead.county)} · ${escapeHtml(labelize(sourceSignal))} · ${escapeHtml(lead.assigned_to || "unassigned")}</small>
        <p>${escapeHtml(lead.next_action || "Review lead evidence")}</p>
        ${leadGenerationSourcePreviewHtml(lead)}
      </div>
      <div class="lead-generation-card-actions">
        <button class="button secondary" type="button" data-lead-action="detail" data-lead-id="${escapeAttr(lead.id)}">Detail</button>
        ${
          lead.primary_source_document_id
            ? `<button class="button secondary" type="button" data-source-document-review="${escapeAttr(lead.primary_source_document_id)}" data-lead-generation-source-review="${escapeAttr(lead.id)}">Review PDF</button>`
            : ""
        }
        <button class="button secondary" type="button" data-lead-action="assign" data-lead-id="${escapeAttr(lead.id)}">Assign</button>
        <button class="button ghost danger" type="button" data-lead-action="suppress" data-lead-id="${escapeAttr(lead.id)}">Suppress</button>
      </div>
    </article>
  `;
}

export function leadGenerationSourcePreviewHtml(lead: LeadGenerationWorkbenchLead): string {
  const type = lead.primary_source_document_type ? labelize(lead.primary_source_document_type) : "source pending";
  const date = lead.primary_source_document_recording_date || lead.primary_source_document_document_date || "date pending";
  const book = lead.primary_source_document_book || "?";
  const page = lead.primary_source_document_page || "?";
  const classification = lead.primary_source_document_classification_status ? labelize(lead.primary_source_document_classification_status) : "classification pending";
  const extraction = lead.primary_source_document_extraction_status ? labelize(lead.primary_source_document_extraction_status) : "extraction pending";
  const summary = lead.primary_source_document_summary || `${type} · ${date} · book ${book} / page ${page}`;
  return `
    <div
      class="lead-generation-source-preview"
      data-lead-generation-source-preview
      data-source-document-id="${escapeAttr(lead.primary_source_document_id || "")}"
      data-source-document-classification-status="${escapeAttr(lead.primary_source_document_classification_status || "")}"
      data-source-document-extraction-status="${escapeAttr(lead.primary_source_document_extraction_status || "")}"
      title="${escapeAttr(summary)}"
    >
      <span>${escapeHtml(type)}</span>
      <span>${escapeHtml(date)}</span>
      <span>${escapeHtml(`book ${book} / page ${page}`)}</span>
      <span>${escapeHtml(classification)} · ${escapeHtml(extraction)}</span>
    </div>
  `;
}

